import { useEffect, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityContextMenuItemBlueprint,
  useEntityPermission,
} from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  alertApiRef,
  createFrontendModule,
  dialogApiRef,
  type DialogApiDialog,
  useApi,
} from '@backstage/frontend-plugin-api';
import { TdsButton, TdsIcon, TdsModal, TdsSpinner } from '@scania/tegel-react';
import { useNavigate } from 'react-router-dom';
import { appRoutePaths } from '../../components/Root/navigation';
import {
  getCatalogEntityLocationRefs,
  resolveCatalogEntityLocation,
  unregisterCatalogEntitySource,
} from './catalogEntityUnregister';
import type { Location } from '@backstage/catalog-client';

type CatalogEntityUnregisterDialogProps = {
  dialog: DialogApiDialog;
  entity: Entity;
  onCompleted: () => void;
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const CatalogEntityUnregisterDialog = ({
  dialog,
  entity,
  onCompleted,
}: CatalogEntityUnregisterDialogProps) => {
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [busy, setBusy] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [location, setLocation] = useState<Location | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let mounted = true;

    setLoadingLocation(true);
    setError(undefined);

    resolveCatalogEntityLocation(entity, catalogApi)
      .then(resolvedLocation => {
        if (mounted) {
          setLocation(resolvedLocation);
        }
      })
      .catch(err => {
        if (mounted) {
          setError(errorMessage(err));
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingLocation(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [catalogApi, entity]);

  const locationRefs = getCatalogEntityLocationRefs(entity);
  const canConfirm = !busy && !loadingLocation;
  const actionTitle = location ? 'Unregister Location' : 'Delete Entity';

  const handleConfirm = async () => {
    setBusy(true);
    setError(undefined);

    try {
      const result = await unregisterCatalogEntitySource(entity, catalogApi);
      const entityName = entity.metadata.title ?? entity.metadata.name;

      alertApi.post({
        display: 'transient',
        message:
          result.type === 'location'
            ? `Unregistered catalog location for ${entityName}`
            : `Deleted catalog entity ${entityName}`,
        severity: 'success',
      });

      dialog.close();
      onCompleted();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      alertApi.post({ message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <TdsModal
      actionsPosition="static"
      closable={!busy}
      header="Unregister entity"
      prevent={busy}
      show
      size="sm"
      tdsAlertDialog="alertdialog"
      onTdsClose={() => {
        if (!busy) {
          dialog.close();
        }
      }}
    >
      <div slot="body" className="tegel-dialog-body">
        <p className="tegel-dialog-copy">
          Are you sure you want to unregister this entity?
        </p>

        {loadingLocation && (
          <span className="tegel-inline-status" role="status">
            <TdsSpinner size="sm" />
            Checking catalog source...
          </span>
        )}

        {!loadingLocation &&
          (location ? (
            <p className="tegel-dialog-copy">
              This will remove the registered catalog location{' '}
              <strong>{location.target}</strong>. Entities imported from that
              location will stop being re-ingested by the catalog.
            </p>
          ) : (
            <p className="tegel-dialog-copy">
              No registered catalog Location was found for this entity. This
              will only delete the current entity row. If GitLab discovery or
              another provider still owns it, the entity may reappear after the
              next refresh.
            </p>
          ))}

        {locationRefs.length > 0 && (
          <div className="tegel-dialog-section">
            <h3>Source reference</h3>
            <ul>
              {locationRefs.map(locationRef => (
                <li key={locationRef}>{locationRef}</li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <p className="tegel-dialog-copy" role="alert">
            {error}
          </p>
        )}
      </div>

      <div slot="actions" className="tegel-dialog-actions">
        <TdsButton
          text="Cancel"
          variant="secondary"
          size="md"
          onClick={() => dialog.close()}
          disabled={busy}
        />
        <TdsButton
          text={busy ? 'Working...' : actionTitle}
          variant="danger"
          size="md"
          onClick={handleConfirm}
          disabled={!canConfirm}
        />
      </div>
    </TdsModal>
  );
};

const drivelineUnregisterEntityContextMenuItem =
  EntityContextMenuItemBlueprint.make({
    name: 'driveline-unregister-entity',
    params: {
      icon: <TdsIcon name="document" size="18px" svgTitle="Unregister" />,
      useProps: () => {
        const { entity } = useEntity();
        const dialogApi = useApi(dialogApiRef);
        const navigate = useNavigate();
        const unregisterPermission = useEntityPermission(
          catalogEntityDeletePermission,
        );

        return {
          disabled: !unregisterPermission.allowed,
          title: 'Unregister Entity',
          onClick: async () => {
            dialogApi.open(({ dialog }) => (
              <CatalogEntityUnregisterDialog
                dialog={dialog}
                entity={entity}
                onCompleted={() => navigate(appRoutePaths.catalog)}
              />
            ));
          },
        };
      },
    },
  });

export const catalogEntityUnregisterModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [drivelineUnregisterEntityContextMenuItem],
});
